import { NumericValidators, required } from '@shared/validators';
import { isValidDate } from '@shared/validators/date-time-validators';
import React from 'react';
import { Control, Controller, DeepRequired, FieldErrorsImpl, GlobalError } from 'react-hook-form';

import { CurrencyTextInput, DateTextInput, NumericTextInput } from '@/components/form-controls';
import { ErrorMessage, Field, Label } from '@/components-new/fieldset';
import { Select } from '@/components-new/select';
import { brandGenericStatusOptions } from '@/features/drugs/types/brand-generic-status';

type DrugPackagingsFormProps = {
  formControl: Control<DrugPackagingFormFields>;
  errors?: Partial<FieldErrorsImpl<DeepRequired<DrugPackagingFormFields>>> & { root?: Record<string, GlobalError> & GlobalError };
};

export type DrugPackagingFormFields = {
  brandGenericStatus: string;
  licenseType: string;
  onMarketWacPrice: string;
  onMarketWacUnitPrice: string;
  cmsOnMarketDate: string;
  wacBeginDate: string;
  baselineCpiu: string;
  baselineCpiuDate: string;
};

export const DrugPackagingsForm = ({ formControl, errors }: DrugPackagingsFormProps) => {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-4">

      <Field>
        <Label>Brand/Generic Status <span className="text-red-500">*</span></Label>
        <Controller
          control={formControl}
          name="brandGenericStatus"
          rules={{ validate: { required: required('Brand/Generic Status is required') } }}
          render={({ field: { onChange, onBlur, value } }) => (
            <Select value={value} onChange={onChange} onBlur={onBlur}>
              <option value="">Select…</option>
              {brandGenericStatusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Select>
          )}
        />
        {errors?.brandGenericStatus?.message && <ErrorMessage>{errors.brandGenericStatus.message}</ErrorMessage>}
      </Field>

      <Field>
        <Label>License Type</Label>
        <Controller
          control={formControl}
          name="licenseType"
          render={({ field: { onChange, onBlur, value } }) => (
            <NumericTextInput onChange={onChange} onBlur={onBlur} value={value} placeholder="e.g. NDA" />
          )}
        />
        {errors?.licenseType?.message && <ErrorMessage>{errors.licenseType.message}</ErrorMessage>}
      </Field>

      <Field>
        <Label>On Market WAC Price</Label>
        <Controller
          control={formControl}
          name="onMarketWacPrice"
          render={({ field: { onChange, onBlur, value } }) => (
            <CurrencyTextInput onChange={onChange} onBlur={onBlur} value={value} placeholder="0.00" />
          )}
        />
        {errors?.onMarketWacPrice?.message && <ErrorMessage>{errors.onMarketWacPrice.message}</ErrorMessage>}
      </Field>

      <Field>
        <Label>CMS On Market Date</Label>
        <Controller
          control={formControl}
          name="cmsOnMarketDate"
          rules={{ validate: isValidDate }}
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTextInput value={value} onChange={e => onChange((e.target as HTMLInputElement).value)} onBlur={onBlur} />
          )}
        />
        {errors?.cmsOnMarketDate?.message && <ErrorMessage>{errors.cmsOnMarketDate.message}</ErrorMessage>}
      </Field>

      <Field>
        <Label>On Market WAC Unit Price</Label>
        <Controller
          control={formControl}
          name="onMarketWacUnitPrice"
          render={({ field: { onChange, onBlur, value } }) => (
            <CurrencyTextInput onChange={onChange} onBlur={onBlur} value={value} placeholder="0.00" />
          )}
        />
        {errors?.onMarketWacUnitPrice?.message && <ErrorMessage>{errors.onMarketWacUnitPrice.message}</ErrorMessage>}
      </Field>

      <Field>
        <Label>WAC Begin Date</Label>
        <Controller
          control={formControl}
          name="wacBeginDate"
          rules={{ validate: isValidDate }}
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTextInput value={value} onChange={e => onChange((e.target as HTMLInputElement).value)} onBlur={onBlur} />
          )}
        />
        {errors?.wacBeginDate?.message && <ErrorMessage>{errors.wacBeginDate.message}</ErrorMessage>}
      </Field>

      <Field>
        <Label>Baseline CPI-U</Label>
        <Controller
          control={formControl}
          name="baselineCpiu"
          rules={{ validate: { isGreaterThan: NumericValidators.isGreaterThan(0) } }}
          render={({ field: { onChange, onBlur, value } }) => (
            <NumericTextInput onChange={onChange} onBlur={onBlur} value={value} maxWholePlaces={3} maxDecimalPlaces={3} placeholder="0.000" />
          )}
        />
        {errors?.baselineCpiu?.message && <ErrorMessage>{errors.baselineCpiu.message}</ErrorMessage>}
      </Field>

      <Field>
        <Label>Baseline CPI-U Date</Label>
        <Controller
          control={formControl}
          name="baselineCpiuDate"
          rules={{ validate: isValidDate }}
          render={({ field: { onChange, onBlur, value } }) => (
            <DateTextInput value={value} onChange={e => onChange((e.target as HTMLInputElement).value)} onBlur={onBlur} />
          )}
        />
        {errors?.baselineCpiuDate?.message && <ErrorMessage>{errors.baselineCpiuDate.message}</ErrorMessage>}
      </Field>

    </div>
  );
};
